import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Timer, Zap } from 'lucide-react';
import { type PokePlayerStats } from '@/services/pokemon';
import { getEvents } from '@/services/events';

interface CatchChargesProps {
  pokeStats: PokePlayerStats;
  /** Rend le contenu sans la carte conteneur (pour l'embarquer dans une GenericCard) */
  flat?: boolean;
}

const BASE_DURATION_MS = 2 * 60 * 60 * 1000;

function computeChargeDurationMs(reductionPercent: number): number {
  const base = 2;
  const actual = Math.max(base * (1 - reductionPercent / 100), 1 / 60);
  return actual * 60 * 60 * 1000;
}

function computeCharges(
  catchCharges: number,
  catchChargeLimit: number,
  rawNextDate: string,
  chargeDurationMs: number
): { charges: number; nextCatchingDate: Date } {
  let charges = catchCharges;
  let nextCatchingDate = new Date(rawNextDate);
  const now = new Date();
  while (nextCatchingDate <= now) {
    if (charges < catchChargeLimit) charges += 1;
    nextCatchingDate = new Date(nextCatchingDate.getTime() + chargeDurationMs);
  }
  return { charges, nextCatchingDate };
}

function formatCountdown(targetMs: number): string {
  const diffMs = targetMs - Date.now();
  if (diffMs <= 0) return 'Prêt !';
  const totalSeconds = Math.floor(diffMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0)
    return `${hours}h ${minutes.toString().padStart(2, '0')}m ${seconds.toString().padStart(2, '0')}s`;
  return `${minutes.toString().padStart(2, '0')}m ${seconds.toString().padStart(2, '0')}s`;
}

export const CatchCharges = ({ pokeStats, flat = false }: CatchChargesProps) => {
  const [, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const { data: eventData } = useQuery({
    queryKey: ['events'],
    queryFn: getEvents,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  const reductionPercent = eventData?.timerReductionPercent ?? 0;
  const chargeDurationMs =
    reductionPercent > 0
      ? computeChargeDurationMs(reductionPercent)
      : BASE_DURATION_MS;

  const { catchChargeLimit } = pokeStats;

  if (catchChargeLimit === 0) {
    const nextDate = new Date(pokeStats.nextCatchingDate);
    const isReady = nextDate.getTime() <= Date.now();
    const inner = (
      <>
        <div className="flex items-center gap-2">
          <div
            className={`flex h-8 w-8 items-center justify-center rounded-full ${isReady ? 'bg-primary-500' : 'bg-primary-500/15'}`}
          >
            <Timer
              size={16}
              className={isReady ? 'text-white' : 'text-primary-500'}
            />
          </div>
          <p className="text-content/60 text-xs font-medium uppercase tracking-wide">
            Prochaine capture
          </p>
        </div>
        <p
          className={`font-lexend text-lg font-semibold leading-tight ${
            isReady ? 'text-primary-500' : 'text-content'
          }`}
        >
          {isReady ? 'Prêt !' : formatCountdown(nextDate.getTime())}
        </p>
      </>
    );
    if (flat) return <div className="flex flex-col gap-2">{inner}</div>;
    return (
      <div
        className={`bg-surface flex flex-col gap-2 rounded-2xl p-4 shadow-md ${isReady ? 'ring-primary-400 ring-2' : ''}`}
      >
        {inner}
      </div>
    );
  }

  const { charges, nextCatchingDate } = computeCharges(
    pokeStats.catchCharges,
    catchChargeLimit,
    pokeStats.nextCatchingDate,
    chargeDurationMs
  );

  const allFull = charges === catchChargeLimit;

  const chargesInner = (
    <>
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div
            className={`flex h-8 w-8 items-center justify-center rounded-full ${allFull ? 'bg-primary-500' : 'bg-primary-500/15'}`}
          >
            <Zap
              size={16}
              className={allFull ? 'text-white' : 'text-primary-500'}
            />
          </div>
          <p className="text-content/60 text-xs font-medium uppercase tracking-wide">
            Charges
          </p>
        </div>
        <span
          className={`font-lexend text-sm font-semibold ${allFull ? 'text-primary-500' : 'text-content/60'}`}
        >
          {charges}/{catchChargeLimit}
        </span>
      </div>

      <div
        className="grid gap-2"
        style={{ gridTemplateColumns: `repeat(${catchChargeLimit}, minmax(0, 1fr))` }}
      >
        {Array.from({ length: catchChargeLimit }, (_, i) => {
          const isFull = i < charges;
          const isInProgress = i === charges && !allFull;
          const slotsAhead = i - charges;

          if (isFull) {
            return (
              <div
                key={i}
                className="bg-primary-500 flex flex-col items-center justify-center rounded-xl py-3 px-2"
              >
                <Zap size={18} className="text-white" />
                <span className="mt-1 text-[10px] font-semibold text-white">
                  Prête
                </span>
              </div>
            );
          }

          if (isInProgress) {
            const countdown = formatCountdown(nextCatchingDate.getTime());
            return (
              <div
                key={i}
                className="border-primary-400 bg-primary-500/10 flex flex-col items-center justify-center rounded-xl border-2 py-3 px-2"
              >
                <Timer size={16} className="text-primary-500" />
                <span className="text-primary-500 mt-1 text-center text-[10px] font-semibold leading-tight">
                  {countdown}
                </span>
              </div>
            );
          }

          const readyAt = nextCatchingDate.getTime() + slotsAhead * chargeDurationMs;
          const countdown = formatCountdown(readyAt);
          return (
            <div
              key={i}
              className="flex flex-col items-center justify-center rounded-xl bg-raised py-3 px-2"
            >
              <Zap size={16} className="text-gray-300" />
              <span className="mt-1 text-center text-[10px] font-semibold leading-tight text-gray-400">
                {countdown}
              </span>
            </div>
          );
        })}
      </div>

      {reductionPercent > 0 && (
        <p className="text-primary-500 mt-2 text-center text-[10px] font-semibold">
          ⚡ Événement actif — durée réduite de {reductionPercent}%
        </p>
      )}
    </>
  );

  if (flat) return <div>{chargesInner}</div>;
  return (
    <div
      className={`bg-surface rounded-2xl p-4 shadow-md ${allFull ? 'ring-primary-400 ring-2' : ''}`}
    >
      {chargesInner}
    </div>
  );
};